import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { SECTIONS, ALL_FIELDS, emptyForm, TABLE_URL } from '../components/RecordFormModal';

const PublicFormPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.client_name?.trim()) e.client_name = 'Client name is required';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); setActiveTab(0); return; }
    setSaving(true);
    try {
      const payload = {};
      ALL_FIELDS.forEach(({ key }) => {
        if (form[key] !== '' && form[key] !== null && form[key] !== undefined) {
          payload[key] = form[key];
        }
      });
      // Add a source flag so we know it came from the public link (optional tracking)
      payload['source'] = 'public';

      const res = await fetch(TABLE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed');
      setSuccess(true);
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field) => {
    const { key, label, type = 'text', options, required } = field;
    const val = form[key] ?? '';
    const hasError = !!errors[key];
    const base = `w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors
      ${hasError ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'}`;

    return (
      <div key={key}>
        <label className="block text-xs font-semibold text-gray-600 mb-1">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        {type === 'textarea' ? (
          <textarea rows={2} value={val} onChange={e => handleChange(key, e.target.value)}
            className={base + ' resize-none'} placeholder={label} />
        ) : type === 'select' ? (
          <select value={val} onChange={e => handleChange(key, e.target.value)} className={base}>
            <option value="">Select…</option>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <input type={type} value={val} onChange={e => handleChange(key, e.target.value)}
            className={base} placeholder={label} />
        )}
        {hasError && <p className="mt-0.5 text-xs text-red-500">{errors[key]}</p>}
      </div>
    );
  };

  const currentSection = SECTIONS[activeTab];

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={32} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h2>
          <p className="text-gray-600 mb-6">Thank you for submitting your application. We will review it and get back to you shortly.</p>
          <button onClick={() => window.location.reload()} className="px-6 py-2 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition">
            Submit Another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-8 text-center">
          <h1 className="text-3xl font-bold text-white mb-2">Loan Application Form</h1>
          <p className="text-primary-100 text-sm max-w-xl mx-auto">Please fill out the details below carefully. Ensure all mandatory fields are completed accurately.</p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6 gap-2 overflow-x-auto bg-gray-50/50 pt-2">
          {SECTIONS.map((s, i) => (
            <button key={s.id} onClick={() => setActiveTab(i)}
              className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors
                ${activeTab === i
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {s.label}
            </button>
          ))}
        </div>

        {/* Form Body */}
        <div className="p-6 md:p-8 flex-1">
          {errors._global && (
            <div className="mb-6 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle size={18} className="shrink-0" />
              {errors._global}
            </div>
          )}
          
          <h3 className="text-lg font-bold text-gray-900 mb-6 pb-2 border-b border-gray-100">
            {activeTab + 1}. {currentSection.label} Details
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {currentSection.fields
              .filter(f => !['dsa_name', 'product_type', 'loan_od', 'status', 'status_description'].includes(f.key))
              .map(renderField)}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 md:px-8 py-5 border-t border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
            Step {activeTab + 1} of {SECTIONS.length}
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setActiveTab(prev => Math.max(0, prev - 1))} 
              disabled={activeTab === 0}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 transition-colors flex items-center gap-1"
            >
              <ChevronLeft size={16} /> Back
            </button>
            
            {activeTab < SECTIONS.length - 1 ? (
              <button 
                onClick={() => setActiveTab(prev => prev + 1)}
                className="px-5 py-2 text-sm font-semibold text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-1 shadow-sm"
              >
                Next <ChevronRight size={16} />
              </button>
            ) : (
              <button 
                onClick={handleSubmit} 
                disabled={saving}
                className="px-6 py-2 text-sm font-bold text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 shadow-sm disabled:opacity-60"
              >
                {saving && <Loader2 size={16} className="animate-spin" />}
                Submit Application
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicFormPage;
